/**
 * Tabla de productos del backoffice (ADM-02, ADM-03).
 *
 * Muestra lo que el comprador no ve: los ocultos y el stock real.
 *
 * Destacar se marca aca y no en la pantalla de Portada porque el buscador del
 * catalogo completo esta aca: para destacar un producto hay que encontrarlo
 * primero entre trescientos. En Portada se ven los que ya estan marcados.
 *
 * **Filtros (22-09-2026).** Con el catalogo real (unos 22 mil SKU) las tres
 * casillas de antes no alcanzaban. Ahora hay estado, foto, precio, stock (con,
 * sin o rango), categorias y marcas (varias), destacados y orden, todos
 * combinables entre si y con el buscador. Viven en la URL
 * (`utilidades/filtros-productos.ts`); la barra es `FiltrosProductos.tsx` y
 * los chips para quitarlos de a uno, `FiltrosActivos.tsx`.
 *
 * **Foto grande y detalle propio (09-09-2026).** La foto de `.mini-img` subio
 * de 40 a 72px (`administracion.css`) para que el producto se reconozca sin
 * leer el nombre, y `porPagina` bajo de 24 a 16 en consecuencia — filas mas
 * altas y las mismas 24 hubieran alargado la pagina de mas. La celda del
 * producto (foto, nombre, SKU) ahora es un `<Link>` a `ProductoDetalle.tsx`;
 * el resto de la fila (destacar, editar SEO) sigue siendo lo que ya era, asi
 * que el link no puede envolver la fila entera —un `<a>` no puede contener un
 * `<button>`—. Viaja la URL actual (pagina, busqueda y filtros) por
 * `location.state` para que "Volver" no devuelva a la pagina 1 sin filtros.
 *
 * **Seleccion y edicion masiva (ADM-03, 10-09-2026).** Solo visibilidad:
 * precio, stock y —desde el 22-09-2026— la categoria son de Random, que los
 * manda en cada sincronizacion. La seleccion es un `Set<id>` que vive en esta
 * pantalla y se limpia sola al cambiar de pagina, de busqueda o de filtro:
 * arrastrarla de una pagina a otra invitaria a aplicar un cambio masivo sobre
 * filas que ya no se estan viendo. `BarraEdicionMasiva.tsx` tiene el resto
 * (los dos modales de confirmacion y la llamada al backend).
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { FormEvent } from 'react';

import type { ActualizarSeoDto, ProductoAdminResumen } from '@elcarpintero/compartido';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { BarraEdicionMasiva } from '../../componentes/administracion/BarraEdicionMasiva';
import { BarraPublicarListos } from '../../componentes/administracion/BarraPublicarListos';
import { FiltrosActivos } from '../../componentes/administracion/FiltrosActivos';
import { FiltrosProductos } from '../../componentes/administracion/FiltrosProductos';
import { FormularioSeo } from '../../componentes/administracion/FormularioSeo';
import { ModalAviso } from '../../componentes/administracion/ModalAviso';
import { Tabla } from '../../componentes/administracion/Tabla';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { Imagen } from '../../componentes/comunes/Imagen';
import { Paginador } from '../../componentes/catalogo/Paginador';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import {
  actualizarSeoProducto,
  obtenerOpcionesFiltroProductos,
  obtenerProductos,
} from '../../servicios/administracion.servicio';
import { destacarProducto } from '../../servicios/portada.servicio';
import {
  conCambio,
  escribirFiltros,
  filtrosActivos,
  leerFiltros,
  sinFiltros,
} from '../../utilidades/filtros-productos';
import type { FiltrosProductos as Filtros } from '../../utilidades/filtros-productos';
import { numero, pesos } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { conPagina, rutas } from '../../utilidades/rutas';

/** Filas por pagina: 16 y no las 24 de siempre, por la foto mas grande. */
const PRODUCTOS_POR_PAGINA = 16;

export function Productos() {
  const [parametros, setParametros] = useSearchParams();
  const pagina = Number(parametros.get('pagina') ?? '1');
  const filtros = leerFiltros(parametros);
  // La cadena es la clave estable de los filtros: el objeto se rehace en cada render.
  const consultaFiltros = escribirFiltros(filtros).toString();
  const termino = filtros.termino ?? '';

  // Sube despues de cada cambio de destacado o de SEO y obliga a pedir la pagina otra vez.
  const [version, setVersion] = useState(0);
  const [marcando, setMarcando] = useState(false);
  // Por que no se pudo destacar (p. ej. la portada ya tiene doce); null lo cierra.
  const [fallaDestacar, setFallaDestacar] = useState<string | null>(null);

  // Producto cuyo modal de SEO esta abierto; null lo mantiene cerrado.
  const [editandoSeo, setEditandoSeo] = useState<ProductoAdminResumen | null>(null);
  const [enviandoSeo, setEnviandoSeo] = useState(false);
  const [fallaSeo, setFallaSeo] = useState<string | null>(null);

  // Seleccion para la edicion masiva (ADM-03). Se limpia sola al cambiar de
  // pagina, busqueda o filtro: seguir viendo "3 seleccionados" sobre una
  // tabla que ya muestra otra cosa induciria a aplicar el cambio a lo que no
  // se esta viendo.
  const [seleccionados, setSeleccionados] = useState<Set<number>>(new Set());
  useEffect(() => {
    setSeleccionados(new Set());
  }, [pagina, consultaFiltros]);

  const { estado, datos, error } = usePeticion(
    (senal) => obtenerProductos({ ...filtros, pagina, porPagina: PRODUCTOS_POR_PAGINA }, senal),
    [pagina, consultaFiltros, version],
  );

  // Lineas, categorias y marcas para los selectores; se piden una vez.
  const opciones = usePeticion((senal) => obtenerOpcionesFiltroProductos(senal), []);
  const activos = filtrosActivos(filtros, opciones.datos);

  function alternarSeleccion(id: number, marcado: boolean): void {
    setSeleccionados((actual) => {
      const siguiente = new Set(actual);
      if (marcado) siguiente.add(id);
      else siguiente.delete(id);
      return siguiente;
    });
  }

  // "Seleccionar todo" marca solo lo que hay en esta pagina, no el catalogo
  // entero: es lo que el administrador esta viendo, no una promesa sobre lo
  // que no ve.
  const idsDePagina = datos?.items.map((producto) => producto.id) ?? [];
  const todoSeleccionado = idsDePagina.length > 0 && idsDePagina.every((id) => seleccionados.has(id));

  function alternarSeleccionTodos(marcado: boolean): void {
    setSeleccionados(marcado ? new Set(idsDePagina) : new Set());
  }

  async function alternarDestacado(id: number, destacado: boolean): Promise<void> {
    setMarcando(true);
    try {
      await destacarProducto(id, destacado);
      setVersion((valor) => valor + 1);
    } catch (fallo) {
      setFallaDestacar(mensajeDe(fallo));
    } finally {
      setMarcando(false);
    }
  }

  async function guardarSeo(datos: ActualizarSeoDto): Promise<void> {
    const producto = editandoSeo;
    if (producto === null) return;

    setFallaSeo(null);
    setEnviandoSeo(true);
    try {
      await actualizarSeoProducto(producto.id, datos);
      setEditandoSeo(null);
      setVersion((valor) => valor + 1);
    } catch (fallo) {
      setFallaSeo(mensajeDe(fallo));
    } finally {
      setEnviandoSeo(false);
    }
  }

  // Cambiar un filtro vuelve a la pagina 1: la pagina 40 de otro resultado
  // no significa nada en este.
  function aplicarFiltros(siguientes: Filtros): void {
    setParametros(escribirFiltros(siguientes));
  }

  function buscar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const valor = new FormData(evento.currentTarget).get('termino');
    const texto = typeof valor === 'string' ? valor.trim() : '';
    aplicarFiltros(conCambio(filtros, { termino: texto.length > 0 ? texto : undefined }));
  }

  // Base para el paginador: sin esto, pasar a la pagina 2 con un filtro o una
  // busqueda activa los perdia en el camino.
  const rutaListado =
    consultaFiltros.length > 0
      ? `${rutas.administracion.productos()}?${consultaFiltros}`
      : rutas.administracion.productos();

  // Con la pagina incluida: es lo que viaja como "volver" al detalle, para
  // no perder el lugar en la tabla al ir y volver.
  const rutaActual = conPagina(rutaListado, pagina);

  return (
    <>
      <Seo
        titulo="Productos"
        descripcion="Catálogo completo desde el backoffice."
        ruta={rutas.administracion.productos()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Productos"
        bajada={
          datos === null
            ? undefined
            : activos.length > 0
              ? `${numero(datos.total)} cumplen los filtros`
              : `${numero(datos.total)} en el catálogo`
        }
        acciones={
          <>
            <form onSubmit={buscar} role="search">
              <label className="solo-lectores" htmlFor="buscar-productos">
                Buscar en el catálogo por nombre, código o marca
              </label>
              {/* `key`: si un chip quita la busqueda, el campo se vacia. */}
              <input
                key={termino}
                id="buscar-productos"
                name="termino"
                type="search"
                className="entrada"
                placeholder="Nombre, código o marca"
                defaultValue={termino}
              />
              <button type="submit" className="btn btn-sm btn-primario">
                <Icono nombre="buscar" />
                Buscar
              </button>
            </form>
          </>
        }
      />

      <div className="admin-cuerpo">
        <FiltrosProductos filtros={filtros} opciones={opciones.datos} onCambiar={aplicarFiltros} />
        <FiltrosActivos
          activos={activos}
          onCambiar={aplicarFiltros}
          onLimpiar={() => {
            aplicarFiltros(sinFiltros(filtros));
          }}
        />

        {estado === 'cargando' ? <Cargando que="los productos" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {datos !== null ? (
          <>
            <BarraPublicarListos
              version={version}
              onAplicado={() => {
                setSeleccionados(new Set());
                setVersion((valor) => valor + 1);
              }}
            />

            <BarraEdicionMasiva
              seleccionados={Array.from(seleccionados)}
              onLimpiar={() => {
                setSeleccionados(new Set());
              }}
              onAplicado={() => {
                setSeleccionados(new Set());
                setVersion((valor) => valor + 1);
              }}
            />

            {datos.items.length === 0 ? (
              <div className="panel-vacio" role="status">
                <p>
                  {activos.length > 0
                    ? 'Ningún producto cumple estos filtros.'
                    : 'El catálogo está vacío.'}
                </p>
                {activos.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-contorno"
                    onClick={() => {
                      aplicarFiltros(sinFiltros(filtros));
                    }}
                  >
                    Limpiar filtros
                  </button>
                ) : null}
              </div>
            ) : (
              <>
                <Tabla
                  titulo="Productos del catálogo"
                  encabezados={
                    <>
                      <th scope="col" className="celda-check">
                        <input
                          type="checkbox"
                          aria-label="Seleccionar todos los productos de esta página"
                          checked={todoSeleccionado}
                          onChange={(evento) => {
                            alternarSeleccionTodos(evento.target.checked);
                          }}
                        />
                      </th>
                      <th scope="col">Producto</th>
                      <th scope="col">Categoría</th>
                      <th scope="col" className="der">
                        Precio
                      </th>
                      <th scope="col" className="der">
                        Stock
                      </th>
                      <th scope="col">Estado</th>
                      <th scope="col">En portada</th>
                      <th scope="col" className="der">
                        Acción
                      </th>
                    </>
                  }
                >
                  {datos.items.map((producto) => (
                    <tr key={producto.id} className={seleccionados.has(producto.id) ? 'fila-seleccionada' : undefined}>
                      <td className="celda-check">
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${producto.nombre}`}
                          checked={seleccionados.has(producto.id)}
                          onChange={(evento) => {
                            alternarSeleccion(producto.id, evento.target.checked);
                          }}
                        />
                      </td>
                      <th scope="row">
                        <Link
                          className="celda-prod"
                          to={rutas.administracion.productoDetalle(producto.id)}
                          state={{ volver: rutaActual }}
                        >
                          {producto.imagen !== null ? (
                            <Imagen
                              className="mini-img"
                              src={producto.imagen.url}
                              alt=""
                              ancho={producto.imagen.ancho}
                              alto={producto.imagen.alto}
                            />
                          ) : null}
                          <span>
                            <span className="n">{producto.nombre}</span>
                            <span className="s">
                              {producto.sku}
                              {/* Precio y stock de la fila son del paquete (VTA-01). */}
                              {producto.unidadesPorPaquete > 1
                                ? ` · paquete de ${numero(producto.unidadesPorPaquete)}`
                                : null}
                            </span>
                          </span>
                        </Link>
                      </th>
                      <td>{producto.categoriaNombre}</td>
                      <td className="der num">
                        {/* Sin precio la tienda no lo muestra, aunque este publicado. */}
                        {producto.precio > 0 ? (
                          pesos(producto.precio)
                        ) : (
                          <span className="etq etq-alerta">Sin precio</span>
                        )}
                      </td>
                      <td className="der num">
                        {producto.estadoStock === 'agotado' ? (
                          <span className="etq etq-critico">Agotado</span>
                        ) : (
                          numero(producto.stock)
                        )}
                      </td>
                      <td>
                        {producto.visible ? (
                          <span className="etq etq-ok">
                            <Icono nombre="ojo" />
                            Publicado
                          </span>
                        ) : (
                          <span className="etq etq-neutra">
                            <Icono nombre="ojoNo" />
                            Oculto
                          </span>
                        )}
                      </td>
                      <td>
                        {/*
                          Un producto oculto no puede destacarse: la portada solo
                          muestra lo publicado, asi que marcarlo no haria nada.
                        */}
                        <button
                          type="button"
                          className="interruptor-boton"
                          aria-pressed={producto.destacado}
                          disabled={marcando || !producto.visible}
                          title={
                            producto.visible
                              ? undefined
                              : 'Un producto oculto no aparece en la portada'
                          }
                          onClick={() => {
                            void alternarDestacado(producto.id, !producto.destacado);
                          }}
                        >
                          <span
                            className={producto.destacado ? 'interruptor on' : 'interruptor'}
                          />
                          <span className="solo-lectores">
                            Destacar {producto.nombre} en la portada
                          </span>
                        </button>
                      </td>
                      <td className="der">
                        <button
                          type="button"
                          className="btn btn-xs btn-contorno"
                          onClick={() => {
                            setFallaSeo(null);
                            setEditandoSeo(producto);
                          }}
                        >
                          Editar SEO
                        </button>
                      </td>
                    </tr>
                  ))}
                </Tabla>

                <Paginador
                  pagina={datos.pagina}
                  total={datos.total}
                  porPagina={datos.porPagina}
                  ruta={rutaListado}
                />
              </>
            )}
          </>
        ) : null}
      </div>

      <FormularioSeo
        entidad={editandoSeo}
        titulo={editandoSeo !== null ? `SEO de ${editandoSeo.nombre}` : ''}
        bajada="Título y descripción del resultado en buscadores"
        placeholderTitulo={editandoSeo?.nombre ?? ''}
        placeholderDescripcion="Se genera sola si se deja vacío"
        enviando={enviandoSeo}
        falla={fallaSeo}
        onCerrar={() => {
          setEditandoSeo(null);
        }}
        onGuardar={(datos) => {
          void guardarSeo(datos);
        }}
      />

      <ModalAviso
        titulo="No se pudo destacar"
        mensaje={fallaDestacar}
        onCerrar={() => {
          setFallaDestacar(null);
        }}
      />
    </>
  );
}
